import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not reset password. Please request a new link.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Reset password" subtitle="This link looks incomplete.">
        <p className="text-sm text-muted-foreground text-center" data-testid="reset-invalid">
          This reset link is missing or invalid.{" "}
          <Link to="/forgot-password" className="text-emerald-700 dark:text-emerald-400 font-semibold hover:underline">Request a new one</Link>.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password" subtitle="Enter a new password for your account.">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div className="space-y-1.5">
          <Label htmlFor="password">New password</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" required data-testid="reset-password-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm">Confirm password</Label>
          <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-type your password" required data-testid="reset-confirm-input" />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full bg-emerald-700 hover:bg-emerald-800 py-2.5" data-testid="reset-submit-button">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Update password
        </Button>
      </form>
    </AuthShell>
  );
}
